import { IconButton, ScopeButton } from '@monorepo/shared-lib';
import { Divider, Image, Space, Typography } from 'antd';
import defaultUserPicture from 'assets/images/default-image.svg';
import { ReactComponent as EditIcon } from 'assets/images/icons/editIcon.svg';
import { ReactComponent as EyeIcon } from 'assets/images/icons/eyeIcon.svg';
import { StyledCard } from 'components/Custom/card';
import { BlackSpan, GraySpan } from 'components/Custom/spans';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AccountType } from 'store/api/enums';
import { LicenseStatus, LicenseType } from 'store/app/enums';
import { User, UserLicence } from 'store/auth/models';
import { pricingPageLink } from '.';

interface ProfileCardProps {
  selectedAccount?: User;
  currentUser?: User;
  profilePicture: string;
  openEditImageWindow: (mode?: 'preview' | 'edit') => void;
  renderUserSubscription: () => React.ReactNode;
  renderRole: (content: string) => React.ReactNode;
  getFullPhoneNumber: () => string;
  redirectPage: (url: string) => void;
  openEditDetails: () => void;
  userLicence?: UserLicence;
}

export const ProfileCard: React.FC<ProfileCardProps> = ({
  selectedAccount,
  currentUser,
  profilePicture,
  openEditImageWindow,
  renderUserSubscription,
  renderRole,
  getFullPhoneNumber,
  redirectPage,
  openEditDetails,
  userLicence,
}) => {
  const { t } = useTranslation();
  const profilePictureAlt = t('users.profile-picture-alt', {
    name: `${selectedAccount?.firstName ?? ''} ${selectedAccount?.lastName ?? ''}`.trim(),
  });
  const isCurrentUser = !selectedAccount?.userUuid
    ? !!currentUser?.userUuid
    : selectedAccount.userUuid === currentUser?.userUuid;

  const getImageSrc = (picture: string) => {
    if (picture.startsWith('data:')) return picture;
    if (picture.startsWith('http')) return picture;
    if (/\.(svg|png|jpe?g|gif|webp|ico)$/i.test(picture)) return picture;
    return `data:image/png;base64,${picture}`;
  };

  return (
    <StyledCard width="100%" className="profile-settings-profile-card">
      {isCurrentUser ? (
        <div className="profile-settings-avatar">
          <div className="profile-image-hover-container">
            <Image
              height={150}
              width={150}
              className="profile-rounded-image profile-settings-avatar-image"
              preview={false}
              alt={profilePictureAlt}
              src={getImageSrc(profilePicture)}
            />
            {profilePicture !== defaultUserPicture && (
              <Space className="profile-image-hover-icons profile-settings-avatar-actions">
                <IconButton
                  type="default"
                  className="icon-button-hover"
                  aria-label={t('common.preview')}
                  style={{
                    height: '30px',
                    borderRadius: 50,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '14px 10px',
                  }}
                  icon={
                    <EyeIcon
                      aria-hidden
                      className="hover-icon"
                      width={'23px'}
                      height={'23px'}
                    />
                  }
                  onClick={() => openEditImageWindow('preview')}
                />
                <IconButton
                  type="default"
                  className="icon-button-hover"
                  aria-label={t('common.edit')}
                  style={{
                    height: '30px',
                    borderRadius: 50,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '14px 10px',
                  }}
                  icon={
                    <EditIcon
                      aria-hidden
                      className="hover-icon"
                      width={'16px'}
                      height={'16px'}
                    />
                  }
                  onClick={() => openEditImageWindow('edit')}
                />
              </Space>
            )}
          </div>
        </div>
      ) : (
        <div className="profile-settings-avatar">
          <Image
            height={150}
            width={150}
            className="profile-rounded-image profile-settings-avatar-image"
            preview={
              profilePicture === defaultUserPicture
                ? false
                : {
                    destroyOnClose: true,
                    toolbarRender: () => null,
                  }
            }
            alt={profilePictureAlt}
            src={getImageSrc(profilePicture)}
          />
        </div>
      )}
      <div className="profile-settings-profile-summary">
      <Typography
        style={{
          fontSize: '18px',
          fontWeight: '400',
          lineHeight: '24px',
          textAlign: 'center',
          color: 'var(--color-text-dark, #042a2a)',
          marginBottom: '2px',
        }}
      >
        {`${selectedAccount?.firstName} ${selectedAccount?.lastName}`}
      </Typography>
      {isCurrentUser && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {renderUserSubscription()}
        </div>
      )}
      <Typography
        style={{
          fontSize: '13px',
          fontWeight: '400',
          lineHeight: '18px',
          textAlign: 'center',
          color: 'var(--color-text-dark, #042a2a)',
          marginBottom: '10px',
        }}
      >
        {selectedAccount?.email}
      </Typography>
      <div className="profile-settings-role">
        {selectedAccount?.accountType &&
          renderRole(selectedAccount?.accountType)}
      </div>
      <Divider />
      <Space direction="vertical" className="profile-settings-details" style={{ marginBottom: '20px' }}>
        {selectedAccount?.companyName && (
          <Space direction="vertical" style={{ rowGap: 0 }}>
            <GraySpan>{t('signup.company-name')}</GraySpan>
            <BlackSpan>{selectedAccount?.companyName}</BlackSpan>
          </Space>
        )}
        {selectedAccount?.phoneNumber && (
          <Space direction="vertical" style={{ rowGap: 0 }}>
            <GraySpan>{t('contacts.phone')}</GraySpan>
            <BlackSpan>{getFullPhoneNumber()}</BlackSpan>
          </Space>
        )}
        {selectedAccount?.website && (
          <Space direction="vertical" style={{ rowGap: 0 }}>
            <GraySpan>{t('common.website')}</GraySpan>
            <BlackSpan>{selectedAccount?.website}</BlackSpan>
          </Space>
        )}
        {selectedAccount?.timezone && (
          <Space direction="vertical" style={{ rowGap: 0 }}>
            <GraySpan>{t('common.timezone')}</GraySpan>
            <BlackSpan>{selectedAccount?.timezone}</BlackSpan>
          </Space>
        )}
      </Space>
      </div>
      <Space direction="vertical" style={{ width: '100%' }}>
        {isCurrentUser &&
          selectedAccount?.accountType &&
          AccountType[selectedAccount?.accountType] == AccountType.OWNER &&
          userLicence?.licenseType === LicenseType.TRIAL &&
          userLicence?.status !== LicenseStatus.UNLIMITED && (
            <ScopeButton
              type="primary"
              width="100%"
              height="32px"
              onClick={() => redirectPage(pricingPageLink)}
            >
              {t('common.pricing-plans')}
            </ScopeButton>
          )}
        {isCurrentUser && (
          <ScopeButton
            type="default"
            width="100%"
            height="32px"
            onClick={openEditDetails}
          >
            {t('common.edit')}
          </ScopeButton>
        )}
      </Space>
    </StyledCard>
  );
};
